import {DEFAULT_ORDER_DETAILS, ORDER_DETAILS_FORMAT_VERSION} from 'consts'
import {useLocalStorageState} from 'hooks/useLocalStorageState'
import {get} from 'lodash'
import React, {useCallback, useContext} from 'react'
import {Basic} from 'types'

type Props = {
  children: React.ReactNode
}

export type CartState = 'opened' | 'closed'

type OrderIntentContextValue = {
  orderIntent: Basic.OrderDetails
  mutators: {
    reset: () => Basic.OrderDetails
    setContact: (contact: Basic.Contact) => Basic.OrderDetails
    setPaymentType: (paymentType: string) => Basic.OrderDetails
    setCrossSellingFlag: (bookingUuid: string, crossSelling: boolean) => Basic.OrderDetails
    toggleCartState: () => Basic.OrderDetails
    pushLocation: (location: string) => Basic.OrderDetails
  }
}

const OrderIntentContext = React.createContext<OrderIntentContextValue>({
  orderIntent: DEFAULT_ORDER_DETAILS,
  mutators: {
    reset: () => DEFAULT_ORDER_DETAILS,
    setContact: () => DEFAULT_ORDER_DETAILS,
    pushLocation: () => DEFAULT_ORDER_DETAILS,
    toggleCartState: () => DEFAULT_ORDER_DETAILS,
    setCrossSellingFlag: () => DEFAULT_ORDER_DETAILS,
    setPaymentType: () => DEFAULT_ORDER_DETAILS,
  },
})

const validator = (data: unknown) => get(data, 'formatVersion') === ORDER_DETAILS_FORMAT_VERSION

export const OrderDetailsProvider = ({children}: Props) => {
  const [orderDetails, setOrderDetails] = useLocalStorageState<Basic.OrderDetails>({
    defaultValue: DEFAULT_ORDER_DETAILS,
    entity: 'orderDetails',
    validator,
  })

  const reset = useCallback(() => setOrderDetails(DEFAULT_ORDER_DETAILS), [setOrderDetails])

  const toggleCartState = useCallback(
    () =>
      setOrderDetails((orderDetails) => ({
        ...orderDetails,
        cartState: orderDetails.cartState === 'closed' ? 'opened' : 'closed',
      })),
    [setOrderDetails]
  )

  const setContact = useCallback(
    (contact: Basic.Contact) => setOrderDetails((orderDetails) => ({...orderDetails, contact})),
    [setOrderDetails]
  )

  const pushLocation = useCallback(
    (location: string) =>
      setOrderDetails((orderDetails) => ({
        ...orderDetails,
        navigationHistory: [...orderDetails.navigationHistory, location],
      })),
    [setOrderDetails]
  )

  const setCrossSellingFlag = useCallback(
    (bookingUuid: string, crossSelling: boolean) =>
      setOrderDetails((orderDetails) => {
        return {
          ...orderDetails,
          crossSelling: {...orderDetails.crossSelling, [bookingUuid]: crossSelling},
        }
      }),
    [setOrderDetails]
  )

  const setPaymentType = useCallback(
    (paymentType: string) => setOrderDetails((orderDetails) => ({...orderDetails, paymentType})),
    [setOrderDetails]
  )

  const value = {
    orderIntent: orderDetails,
    mutators: {
      reset,
      setContact,
      pushLocation,
      toggleCartState,
      setCrossSellingFlag,
      setPaymentType,
    },
  }

  return <OrderIntentContext.Provider value={value}>{children}</OrderIntentContext.Provider>
}

export const useOrderIntentContext = () => {
  return useContext(OrderIntentContext)
}

export const useOrderDetails = () => {
  const {orderIntent} = useOrderIntentContext()
  return orderIntent
}

export const useContact = () => {
  const {orderIntent} = useOrderIntentContext()
  return orderIntent.contact
}

export const useCartState = () => {
  const {orderIntent} = useOrderIntentContext()
  return orderIntent.cartState
}

export const usePaymentType = () => {
  const {orderIntent} = useOrderIntentContext()
  return orderIntent.paymentType
}

export const useCrossSelling = () => {
  const {orderIntent} = useOrderIntentContext()
  return orderIntent.crossSelling
}

export const useNavigationHistory = () => {
  const {orderIntent} = useOrderIntentContext()
  return orderIntent.navigationHistory
}

export const useOrderDetailsMutators = () => {
  const {mutators} = useOrderIntentContext()
  return mutators
}
